import { ThemeProvider, createTheme } from "@mui/material/styles";
import GlobalStyles from "@mui/material/GlobalStyles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";

const PICKER_DIALOG_CLASS = "pace-clock-picker-dialog";

const lightPickerTheme = createTheme({
  palette: { mode: "light" },
  zIndex: { modal: 13000 },
});

function pickerToolbarStyles() {
  const idle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "hsl(var(--ds-bg))",
    border: "1px solid hsl(var(--ds-border))",
    boxShadow: "none",
    backdropFilter: "none",
  };
  const selected = {
    backgroundColor: "hsl(var(--ds-primary))",
    border: "1px solid hsl(var(--ds-primary))",
    boxShadow: "none",
  };

  const root = `.${PICKER_DIALOG_CLASS}`;
  return {
    [`${root} .MuiPickersToolbar-root`]: {
      paddingBottom: 8,
    },
    [`${root} .MuiPickersToolbar-content`]: {
      gap: 10,
      padding: 0,
      background: "transparent",
      border: "none",
      boxShadow: "none",
      backdropFilter: "none",
    },
    [`${root} .MuiTimePickerToolbar-hourMinuteLabel`]: {
      alignItems: "center",
      gap: 2,
    },
    [`${root} .MuiTimePickerToolbar-separator`]: {
      color: "hsl(var(--ds-muted-fg)) !important",
      fontWeight: 600,
    },
    [`${root} .MuiTimePickerToolbar-hourMinuteLabel .MuiButton-root`]: {
      minWidth: 52,
      minHeight: 40,
      padding: "2px 8px",
      borderRadius: 8,
      ...idle,
    },
    [`${root} .MuiTimePickerToolbar-hourMinuteLabel .MuiButton-root .MuiPickersToolbarText-root`]: {
      color: "hsl(var(--ds-fg))",
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.1,
    },
    [`${root} .MuiTimePickerToolbar-hourMinuteLabel .MuiButton-root:has([data-selected])`]: selected,
    [`${root} .MuiTimePickerToolbar-hourMinuteLabel .MuiPickersToolbarText-root[data-selected]`]: {
      color: "hsl(var(--ds-primary-fg)) !important",
    },
    [`${root} .MuiTimePickerToolbar-ampmSelection`]: {
      gap: 4,
      marginLeft: 4,
    },
    [`${root} .MuiTimePickerToolbar-ampmSelection .MuiButton-root`]: {
      minWidth: 44,
      minHeight: 26,
      padding: "0 8px",
      borderRadius: 6,
      ...idle,
    },
    [`${root} .MuiTimePickerToolbar-ampmSelection .MuiButton-root .MuiPickersToolbarText-root`]: {
      color: "hsl(var(--ds-muted-fg))",
      fontWeight: 600,
      fontSize: "0.72rem",
      letterSpacing: "0.04em",
    },
    [`${root} .MuiTimePickerToolbar-ampmSelection .MuiButton-root:has([data-selected])`]: selected,
    [`${root} .MuiTimePickerToolbar-ampmSelection .MuiPickersToolbarText-root[data-selected]`]: {
      color: "hsl(var(--ds-primary-fg)) !important",
    },
    [`${root} .MuiClock-pin`]: {
      backgroundColor: "hsl(var(--ds-primary))",
    },
    [`${root} .MuiClockPointer-root`]: {
      backgroundColor: "hsl(var(--ds-primary))",
    },
    [`${root} .MuiClockPointer-thumb`]: {
      backgroundColor: "hsl(var(--ds-primary))",
      borderColor: "hsl(var(--ds-primary))",
    },
    [`${root} .MuiClockNumber-selected`]: {
      color: "hsl(var(--ds-primary-fg)) !important",
      backgroundColor: "hsl(var(--ds-primary))",
    },
    [`${root} .MuiPickersLayout-actionBar .MuiButton-root`]: {
      color: "hsl(var(--ds-primary))",
    },
    [`${root} .MuiPickersArrowSwitcher-button`]: {
      color: "hsl(var(--ds-primary))",
    },
  };
}

function hhmmToDate(hhmm) {
  const s = String(hhmm || "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const d = new Date();
  d.setSeconds(0, 0);
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d;
}

function dateToHhmm(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function TwelveHourClockTimeField({
  id,
  value = "",
  onChange,
  required = false,
  ariaLabel = "Time",
}) {
  const pickerValue = hhmmToDate(value);

  const commit = (next) => {
    if (next instanceof Date && !Number.isNaN(next.getTime())) {
      onChange?.(dateToHhmm(next));
      return;
    }
    if (next == null) onChange?.("");
  };

  return (
    <div className="relative mt-1.5">
      <ThemeProvider theme={lightPickerTheme}>
        <GlobalStyles styles={pickerToolbarStyles} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MobileTimePicker
            ampm
            minutesStep={1}
            views={["hours", "minutes"]}
            value={pickerValue}
            onChange={commit}
            onAccept={commit}
            localeText={{ toolbarTitle: "SELECT TIME" }}
            slotProps={{
              field: { readOnly: false },
              textField: {
                id,
                required,
                fullWidth: true,
                size: "small",
                variant: "outlined",
                inputProps: { "aria-label": ariaLabel },
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "hsl(var(--ds-surface))",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "hsl(var(--ds-primary))",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused": {
                    boxShadow: "0 0 0 3px hsl(var(--ds-ring))",
                  },
                },
              },
              actionBar: { actions: ["cancel", "accept"] },
              layout: { className: PICKER_DIALOG_CLASS },
              dialog: {
                disableRestoreFocus: true,
                className: PICKER_DIALOG_CLASS,
                PaperProps: { className: PICKER_DIALOG_CLASS },
                sx: { zIndex: 13000 },
              },
            }}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
}
